import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { selectProjectsPagination, setProjectsPagination } from '../../model/projectsSlice'
import { IPagination } from '../../../../shared/types/Pagination.interface'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ColumnsType } from 'antd/lib/table'
import { Table } from '../../../../lib/ui/table/components/table/Table'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { hideModal, setModal } from '../../../../app/store/reducers/AppSlice'
import { AddProjectModal, AddProjectRef } from '../add-project-modal/AddProjectModal'
import { IconButton } from '../../../../shared/components/buttons/icon-button/IconButton'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { IconPlus } from '../../../../shared/components/icons/IconPlus'
import {
  removeNotification,
  selectTariffDetails,
  selectUser,
  setTariffUpdated,
} from '../../../../app/store/reducers/AuthSlice'
import { TariffButton } from '../../../../shared/components/buttons/tariff-button/TariffButton'
import { IconCrossBold } from '../../../../shared/components/icons/IconCrossBold'
import {
  GetProjectListApiArg,
  PostProjectUpdateStatusApiArg,
  ProjectListRow,
  useGetFilterProjectsQuery,
  useGetProjectListQuery,
  usePostProjectUpdateStatusMutation,
  usePostSiteHideNotificationMutation,
} from '../../../../app/api'
import { IconArrowRight } from '../../../../shared/components/icons/IconArrowRight'
import { UnsavedChangesModal } from '../../../project/widgets/unsaved-changes-modal/UnsavedChangesModal'
import { NameCell } from '../../ui/cells/NameCell'
import { CountriesCell } from '../../ui/cells/CountriesCell'
import { StatusCell } from '../../ui/cells/StatusCell'
import { KeywordsCountCell } from '../../ui/cells/KeywordsCountCell'
import { ActionsCell } from '../../ui/cells/ActionsCell'
import styles from './ProjectsTable.module.scss'
import { IconPlay } from '../../../../shared/components/icons/IconPlay'
import { IconPause } from '../../../../shared/components/icons/IconPause'
import { IconArchive } from '../../../../shared/components/icons/IconArchive'
import { IconRestore } from '../../../../shared/components/icons/IconRestore'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'

const AddProjectWithRef = React.forwardRef<AddProjectRef>((props, ref) => {
  return <AddProjectModal ref={ref} />
})

AddProjectWithRef.displayName = 'AddProjectWithRef'

type ProjectStatus = NonNullable<PostProjectUpdateStatusApiArg['body']['status']>

export const ProjectsTable = () => {
  const { t } = useTranslation()
  const user = useAppSelector(selectUser)
  const [showExploreDemo, setShowExploreDemo] = useState(false)
  const tariffDetails = useAppSelector(selectTariffDetails)
  const navigate = useNavigate()
  const { current, pageSize } = useAppSelector<IPagination>(selectProjectsPagination)
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([])
  const [selectedRows, setSelectedRows] = useState<ProjectListRow[]>([])
  const [projectIds, setProjectIds] = useState<string[]>([])
  const dispatch = useAppDispatch()
  const addProjectModalRef = useRef<AddProjectRef | null>(null)

  const [queryParams, setQueryParams] = useState<GetProjectListApiArg>({
    page: current,
    'per-page': pageSize,
  })
  const { data, isLoading, isFetching } = useGetProjectListQuery(queryParams)

  const [bulkUpdateStatus] = usePostProjectUpdateStatusMutation()

  const {
    data: projects,
    isFetching: projectsFetching,
    isLoading: projectsLoading,
  } = useGetFilterProjectsQuery({ all: true })

  const sortedItems = useMemo(() => {
    if (!data?.data?.items) return []

    return [
      ...data.data.items.filter((item) => !item.is_demo),
      ...data.data.items.filter((item) => item.is_demo),
    ]
  }, [data])

  const modifiedData = useMemo(() => {
    if (!data) return data

    return {
      ...data,
      data: {
        ...data.data,
        items: sortedItems,
      },
    }
  }, [data, sortedItems])
  const [hideNotification] = usePostSiteHideNotificationMutation()

  useEffect(() => {
    const notification = user.notifications?.find(
      (notification) => notification.type === 'explore_demo_reports_block',
    )

    if (notification && !notification.is_hidden) {
      setShowExploreDemo(true)
    }
  }, [user])

  useEffect(() => {
    if (showExploreDemo) {
      setExpandedRowKeys(
        data?.data?.items?.filter((value) => value.is_demo).map((value) => value.id) || [],
      )
    }
  }, [data, showExploreDemo])

  const columns: ColumnsType<ProjectListRow> = [
    {
      title: t('table.columns.name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (_: string, record) => <NameCell {...record} />,
    },
    {
      title: t('table.columns.countries'),
      key: 'countries',
      render: (_: string, record) => <CountriesCell {...record} />,
    },
    {
      title: t('table.columns.status'),
      key: 'status',
      render: (_: string, record) => <StatusCell {...record} />,
    },
    {
      title: () => (
        <span className={styles.columnTitle}>
          {t('table.columns.adsScan')}
          <BpTooltip title={t('pages.projects.adsScanTooltip')} />
        </span>
      ),
      key: 'periodicity',
      render: (_: string, { periodicity }) => {
        return <div>{periodicity ? periodicity : '—'}</div>
      },
      width: 150,
    },
    {
      title: () => (
        <span className={styles.columnTitle}>
          {t('table.columns.organicScan')}
          <BpTooltip title={t('pages.projects.organicScanTooltip')} />
        </span>
      ),
      dataIndex: 'seo_periodicity',
      key: 'seo_periodicity',
      width: 150,
    },
    {
      title: () => (
        <span className={styles.columnTitle}>
          {t('table.columns.keywords')}
          <BpTooltip title={t('pages.projects.keywordsTooltip')} />
        </span>
      ),
      dataIndex: 'keywords_count',
      key: 'keywords_count',
      render: (_: string, record) => <KeywordsCountCell {...record} />,
      width: 100,
    },
    {
      title: t('table.columns.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
    },
    {
      title: t('table.columns.updated'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
    },
    {
      key: 'actions',
      render: (_: ProjectListRow, record, index) => <ActionsCell {...record} index={index} />,
      width: '150px',
      fixed: 'right',
    },
  ]

  const hideNotify = () => {
    const notification = user.notifications?.find(
      (notification) => notification.type === 'explore_demo_reports_block',
    )

    if (notification) {
      hideNotification({ id: notification.id })
      dispatch(removeNotification(notification.type))
    }

    setShowExploreDemo(false)
  }

  const handlePaginationChange = (page: number, pageSize: number, total: number) => {
    dispatch(
      setProjectsPagination({
        current: page,
        pageSize: pageSize,
        total: total,
      }),
    )
    setQueryParams({
      ...queryParams,
      page: page,
      'per-page': pageSize,
    })
  }

  const handleSortingChange = (sorter: string) => {
    setQueryParams({
      ...queryParams,
      sort: sorter,
    })
  }

  const applyProjectsFilter = () => {
    setSelectedRows([])
    dispatch(
      setProjectsPagination({
        current: 1,
        pageSize: pageSize,
        total: data?.data?.total || 0,
      }),
    )
    setQueryParams({
      ...queryParams,
      page: 1,
      projectIds: projectIds.length ? projectIds.join(',') : undefined,
    })
  }

  const handleBulkStatusChange = (status: string) => {
    // ids строкой через запятую, как раньше: массив слой отправил бы JSON-строкой «[1,2]»,
    // из которой бэк (explode по запятой) потеряет первый id
    bulkUpdateStatus({
      body: {
        ids: selectedRows.map((row) => row.id).join(',') as unknown as number[],
        status: status as ProjectStatus,
      },
    }).then(() => dispatch(setTariffUpdated()))
    setSelectedRows([])
  }

  const Toolbar = () => {
    const actions = [
      {
        text: t('pages.projects.activate'),
        value: 'active',
        hidden: !selectedRows.some((row) => !['Active'].includes(row.status)),
        icon: IconPlay,
      },
      {
        text: t('pages.projects.pause'),
        value: 'pause',
        hidden: !selectedRows.some((row) => !['On Pause', 'Archived'].includes(row.status)),
        icon: IconPause,
      },
      {
        text: t('pages.projects.archive'),
        value: 'archive',
        hidden: !selectedRows.some((row) => row.status !== 'Archived'),
        icon: IconArchive,
      },
    ]

    return (
      <div className={'flex items-center justify-between'}>
        <div className={'flex items-center gap-[24px]'}>
          <p className={'font-medium text-[#6684A7]'}>
            {t('other.countSelected', { count: selectedRows.length })}
          </p>
          <div className={'flex items-center gap-[24px]'}>
            {actions
              .filter((action) => !action.hidden)
              .map(({ icon: Icon, ...action }) => (
                <button
                  key={action.text}
                  className={'flex gap-[4px] text-[#A059FF]'}
                  onClick={() => handleBulkStatusChange(action.value)}
                >
                  <Icon className={'w-[16px]'} color={'#a059ff'} /> {action.text}
                </button>
              ))}
          </div>
        </div>
        <button className={'text-[#DC2F02]'} onClick={() => setSelectedRows([])}>
          {t('buttons.clearSelection')}
        </button>
      </div>
    )
  }

  const handleAddProjectModal = () => {
    const handleClose = () => {
      if (addProjectModalRef?.current?.hasUnsavedChanges()) {
        dispatch(
          setModal({
            children: (
              <UnsavedChangesModal
                onLeave={() => {
                  dispatch(hideModal())
                  dispatch(hideModal())
                }}
              />
            ),
            size: 'md',
          }),
        )
      } else {
        dispatch(hideModal())
      }
    }

    dispatch(
      setModal({
        children: (
          <AddProjectModal
            ref={(ref) => {
              addProjectModalRef.current = ref
            }}
          />
        ),
        onClose: handleClose,
      }),
    )
  }

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <div className={'flex items-center gap-[16px]'}>
          <BpButton
            disabled={
              !tariffDetails.tariff ||
              tariffDetails.tariff.used_projects_count >= tariffDetails.tariff.max_projects ||
              tariffDetails.tariff.is_subscription_expired
            }
            contentClassName={styles.addButton}
            iconLeft={<IconPlus />}
            size={'sm'}
            onClick={handleAddProjectModal}
          >
            {t('pages.projects.addProject')}
          </BpButton>
          <BpSelect
            multiple
            showSearch
            className={'w-[192px]'}
            disabled={projectsLoading}
            enableChipLoad={false}
            loading={projectsFetching || projectsLoading}
            options={projects}
            placeholder={t('fields.other.selectFromList')}
            value={projectIds}
            onChange={(e) =>
              setProjectIds(
                [].slice.call(e.target.selectedOptions).map((option: IOption) => option.value),
              )
            }
            onDropClose={applyProjectsFilter}
          />
        </div>
        <TariffButton />
      </div>
      <Table
        stickyExpandedRow
        expandedRowRender={(record: any) =>
          showExploreDemo && (
            <div className={styles.expandedRow}>
              <IconButton className={styles.closeButton} onClick={hideNotify}>
                <IconCrossBold color='#6684A7' />
              </IconButton>
              <div className={styles.expandedContent}>
                <h2 className={styles.expandedTitle}>{t('pages.projects.exploreDemo')}</h2>
              </div>
              <div className={styles.cardsRow}>
                <div className={styles.card}>
                  <div className={styles.cardContent}>
                    <p className={styles.cardLabel}>{t('pages.projects.SERPMonitoring')}</p>
                    <h2 className={styles.cardTitle}>{t('pages.projects.paidResults')}</h2>
                    <p className={styles.cardDesc}>{t('pages.projects.trackPaidAds')}</p>
                  </div>
                  <div className={styles.cardAction}>
                    <BpButton
                      className={styles.cardButton}
                      iconRight={<IconArrowRight />}
                      onClick={() => navigate(`/ad-reports?project=${record.id}`)}
                    >
                      {t('buttons.goToReport')}
                    </BpButton>
                  </div>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardContent}>
                    <p className={styles.cardLabel}>{t('pages.projects.nonCompliantAffiliates')}</p>
                    <h2 className={styles.cardTitle}>{t('pages.projects.PPCCompliance')}</h2>
                    <p className={styles.cardDesc}>{t('pages.projects.ensureAffiliatesFollow')}</p>
                  </div>
                  <div className={styles.cardAction}>
                    <BpButton
                      className={styles.cardButton}
                      iconRight={<IconArrowRight />}
                      onClick={() => navigate(`/ppc-compliance?project=${record.id}`)}
                    >
                      {t('buttons.goToReport')}
                    </BpButton>
                  </div>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardContent}>
                    <p className={styles.cardLabel}>{t('pages.projects.SERPMonitoring')}</p>
                    <h2 className={styles.cardTitle}>{t('pages.projects.organicResults')}</h2>
                    <p className={styles.cardDesc}>{t('pages.projects.monitorOrganicRankings')}</p>
                  </div>
                  <div className={styles.cardAction}>
                    <BpButton
                      className={styles.cardButton}
                      iconRight={<IconArrowRight />}
                      onClick={() => navigate(`/organic-results?project=${record.id}`)}
                    >
                      {t('buttons.goToReport')}
                    </BpButton>
                  </div>
                </div>
              </div>
            </div>
          )
        }
        columns={columns}
        data={modifiedData}
        expandedRowKeys={expandedRowKeys}
        loading={isLoading || isFetching}
        rowKey={(record) => record.id}
        rowSelectionDisabled={(record) => record.is_demo}
        // таблица шириной в сумму колонок: заданные ширины не ужимаются, не хватает места — прокрутка
        scrollX={'max-content'}
        selectedRowKeys={selectedRows}
        toolbar={selectedRows.length ? <Toolbar /> : null}
        onPaginationChange={handlePaginationChange}
        onRowSelectionChange={(selection) => setSelectedRows(selection)}
        onSortingChange={handleSortingChange}
      />
    </section>
  )
}
